import { ArrowDownIcon, LinkBreakIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import * as Dialog from '@radix-ui/react-dialog'
import { DistributiveOmit } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { Repository, RepositoryIntegration } from '../../utils/api-client/api-schemas'
import {
  convertIntegrationTypeToTool,
  getRepositoryIdentifierLabels,
  getToolName,
} from '../../utils/repository-utils.tsx'
import { joinWithAnd, pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button'
import { GhostButton } from '../buttons/ghost-button'
import { SCMIcon } from '../third-party-branding/scm-icon'
import { ToolIcon } from '../third-party-branding/tool-icon'
import * as styles from './remove-integration-modal.css.ts'

type RemoveIntegrationModalProps = {
  repository: DistributiveOmit<Repository, '_links'>
  repositoryIntegrations?: DistributiveOmit<RepositoryIntegration, '_links'>[]
  integrationToRemove: string | null
  setIntegrationToRemove: (integrationToRemove: string | null) => unknown
  onCancelRemoveMapping: () => unknown
  onConfirmRemoveMapping: () => unknown
}

export const RemoveIntegrationModal: React.FC<RemoveIntegrationModalProps> = ({
  repository,
  repositoryIntegrations,
  integrationToRemove,
  setIntegrationToRemove,
  onCancelRemoveMapping,
  onConfirmRemoveMapping,
}) => {
  const toolToRemove = useMemo(() => {
    const integration = repositoryIntegrations?.find(item => item.id === integrationToRemove)
    return integration ? convertIntegrationTypeToTool(integration.type) : undefined
  }, [repositoryIntegrations, integrationToRemove])

  const identifierLabels = getRepositoryIdentifierLabels(toolToRemove)

  return (
    <Dialog.Root open={true} onOpenChange={() => setIntegrationToRemove(null)}>
      <Dialog.Portal>
        <Dialog.Content className={styles.confirmationDialogContent}>
          <Dialog.Title className={styles.confirmationDialogTitle}>
            <LinkBreakIcon
              size={32}
              weight="light"
              style={{
                color: mappedColors.secondary.spark,
              }}
            />
            Remove mapping?
          </Dialog.Title>
          <Dialog.Description asChild>
            <div className={styles.confirmationDialogDescription}>
              <div className={styles.confirmationDialogTextContainer}>
                <p className={styles.confirmationDialogText}>You are about to remove the following tool mapping:</p>
              </div>

              <div className={styles.confirmationDialogHighlightBox}>
                <div className={styles.confirmationDialogRepositoryInfo}>
                  <SCMIcon type={repository.type} size="medium" />
                  <span>{repository.full_name}</span>
                </div>

                <div className={styles.confirmationDialogArrowContainer}>
                  <ArrowDownIcon size={16} weight="light" />
                </div>

                <div className={styles.confirmationDialogToolInfo}>
                  {toolToRemove && (
                    <>
                      <ToolIcon tool={toolToRemove} size="medium" />
                      <span>{getToolName(toolToRemove)}</span>
                    </>
                  )}
                </div>
              </div>

              <div className={styles.confirmationDialogTextContainer}>
                {/* Naming what is discarded matters most to the user who is only trying to change
                    which instance answers: there is no way to edit a mapping in place, so they have
                    to remove and re-add, and the identifier goes with the mapping.

                    Shown only for a tool that has one. A Polaris mapping is auto-created from the
                    SARIF, so there is no identifier the user entered and nothing to ask for again —
                    promising otherwise is worse than saying nothing.

                    Every identifier, not just the first: a Hub mapping stores a project and a
                    version, and naming one of the two understates what has to be re-entered. */}
                {identifierLabels.length > 0 && (
                  <p className={styles.confirmationDialogText}>
                    The {joinWithAnd([...identifierLabels])} stored for this tool{' '}
                    {pluralize(identifierLabels.length, 'is', 'are')} removed with it, and reconnecting asks for{' '}
                    {pluralize(identifierLabels.length, 'it', 'them')} again.
                  </p>
                )}
                <p className={styles.confirmationDialogText}>This action cannot be undone. Do you wish to proceed?</p>
              </div>
            </div>
          </Dialog.Description>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
            <GhostButton type="secondary" onClick={onCancelRemoveMapping}>
              Cancel
            </GhostButton>
            <DefaultButton
              buttonType="destructive"
              icon={<LinkBreakIcon size={16} weight="regular" />}
              onClick={onConfirmRemoveMapping}
            >
              Remove mapping
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
